import React, { useCallback, useEffect, useState } from 'react'
import { useParams, useSearchParams, useNavigate, generatePath } from 'react-router-dom'
import {
  Box,
  Skeleton
} from '@mui/material'
import { useEvent, useEventsOnViewLoad } from '@emerald-works/react-event-bus-client'
import { useAlert, usePromiseEvent, useTranslation } from '../../hooks'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import { skillBiteSlice, userSlice } from '../../reducers'
import { SkillBiteBreadcrumbs } from './components/skill-bite-breadcrumbs'
import { SkillBiteNavigation } from './components/skill-bite-navigation'
import { SkillBiteViewer } from './components/skill-bite-viewer'
import { SkillBiteContainer } from './components/skill-bite-container'
import { cloneDeep } from 'lodash'
import { SkillBitesIcon } from '../skill-bites/components/skill-bites-icon'
import { routes } from '../../routes'
import { SkillBiteLoadErrorModal } from './components/skill-bite-load-error-modal'
import { ToolkitPrivateRoute } from './../../components/toolkit-private-route'
import { SkillBiteTranslateDialog } from '../skill-bites/components/translate-dialog'
import { SkillBiteTranslateButton } from '../skill-bites/components/translate-button'

const pageCache = {}

const buildCacheKey = (id, language) => (`${id}#${language}`)

const setPageCache = (id, language, payload) => {
  const cacheKey = buildCacheKey(id, language)
  pageCache[cacheKey] = payload
}

const getPageCache = (id, language) => {
  const cacheKey = buildCacheKey(id, language)
  return pageCache[cacheKey]
}

const SkillBiteSessionPage = () => {
  const { skillBiteId, sessionId } = useParams()
  const { t } = useTranslation('skill_bite')
  const skillBite = useSelector(skillBiteSlice.selectors.selectSkillBite)
  const session = useSelector(skillBiteSlice.selectors.selectSession)
  const currentPage = useSelector(skillBiteSlice.selectors.selectCurrentPage)
  const skillBiteLanguage = useSelector(userSlice.selectors.selectSkillBiteLanguage)
  const [searchParams] = useSearchParams()
  const from = searchParams.get('from')
  const isPreview = searchParams.has('preview')
  const [errorModalConfig, setErrorModalConfig] = useState({
    open: false,
    title: '',
    description: ''
  })
  const [translateDialogOpen, setTranslateDialogOpen] = useState(false)
  const navigate = useNavigate()

  const saveCommitmentPromise = usePromiseEvent('skillBiteSaveCommitment')
  const saveCommitmentCheckPromise = usePromiseEvent('skillBiteSaveCommitmentCheck')
  const { enqueueMsg } = useAlert()
  const dispatch = useDispatch()

  const setCurrentPage = useCallback((page) => dispatch(skillBiteSlice.actions.setCurrentPage(page)), [dispatch])
  const [trackStartSession] = useEvent([skillBiteSlice.eventBus.trackStartSession])

  const [getSkillBite] = useEvent([skillBiteSlice.eventBus.getSkillBite])
  const [getSkillBiteSession] = useEvent([skillBiteSlice.eventBus.getSkillBiteSession({
    onSuccess: useCallback(() => {
      if (!trackStartSession.hasBeenTriggered && !isPreview) {
        trackStartSession.trigger({ skillBiteId, sessionId, from })
      }
    }, [trackStartSession, skillBiteId, sessionId, from, isPreview]),
    onError: (err) => {
      console.log('Failed to get skill-bite session', err)
      const { code } = err
      if (code === 'ERR_USER_NOT_SUBSCRIBED') {
        setErrorModalConfig({
          open: true,
          title: t('subscription_required_title'),
          description: t('subscription_required_description')
        })
        return
      }

      if (code === 'ERR_SESSION_NOT_AVAILABLE') {
        setErrorModalConfig({
          open: true,
          title: t('session_not_available_title'),
          description: t('session_not_available_description')
        })
        return
      }

      enqueueMsg(t('modal_error_description'), 'error', t('error'))
    }
  })])

  const [getPage] = useEvent([skillBiteSlice.eventBus.getPage({
    onSuccess: useCallback((payload) => {
      const language = payload.blocks[0]?.language ?? skillBiteLanguage
      setPageCache(payload.id, language, payload)
    }, [skillBiteLanguage])
  })])

  const [saveQuizAnswer] = useEvent([skillBiteSlice.eventBus.saveQuizAnswer])

  useEventsOnViewLoad(() => {
    if (!getSkillBite.hasBeenTriggered) {
      getSkillBite.trigger({ skillBiteId, from, isPreview, language: skillBiteLanguage })
    }

    if (!getSkillBiteSession.hasBeenTriggered) {
      getSkillBiteSession.trigger({ skillBiteId, sessionId, from, isPreview, language: skillBiteLanguage })
    }
  }, [getSkillBite, getSkillBiteSession, skillBiteId, sessionId, isPreview, from])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    if (currentPage.id) {
      window.scrollTo(0, 0)
      const cachedPage = getPageCache(currentPage.id, skillBiteLanguage)

      if (!cachedPage) {
        getPage.trigger({ skillBiteId, sessionId, pageId: currentPage.id, language: skillBiteLanguage })
      } else {
        setCurrentPage(cachedPage)
      }
    }
  }, [currentPage.id, skillBiteId, sessionId, getPage, skillBiteLanguage, setCurrentPage])

  useEffect(() => {
    return () => {
      if (dispatch) {
        Object.keys(pageCache).forEach(key => delete pageCache[key])
        dispatch(skillBiteSlice.actions.reset())
      }
    }
  }, [dispatch])

  const handleSaveQuiz = useCallback(({ quiz, score }) => {
    if (isPreview) {
      console.log('handle save quiz in preview mode')
      return
    }
    const payload = {
      id: quiz.id,
      questions: quiz.questions.map(question => {
        const { checked, ...checkedChoice } = question.choices.find(choice => choice.checked)
        return {
          id: question.id,
          title: question.title,
          selectedChoice: checkedChoice
        }
      }),
      userScore: score
    }

    const currentPageCache = cloneDeep(getPageCache(currentPage.id, skillBiteLanguage))

    let currentQuizEntityMapKey
    const currentPageQuizBlockIndex = currentPageCache.blocks.findIndex(block => {
      return block.type === 'draftjs' && Object.entries(block?.data?.entityMap).some(([key, value]) => {
        const isQuiz = value.type === 'ew-quiz' && value.data.quiz.id === quiz.id
        currentQuizEntityMapKey = key
        return isQuiz
      })
    })

    if (currentPageQuizBlockIndex !== -1) {
      setPageCache(currentPage.id, skillBiteLanguage, {
        ...currentPageCache,
        blocks: currentPageCache.blocks.map((block, index) => {
          if (index !== currentPageQuizBlockIndex) return block
          block.data.entityMap[currentQuizEntityMapKey].data.quiz = quiz
          return block
        })
      })
    }

    saveQuizAnswer.trigger({
      skillBiteId,
      quiz: payload
    })
  }, [saveQuizAnswer, skillBiteId, currentPage, isPreview, skillBiteLanguage])

  const handleSaveCommitment = useCallback(async ({ commitmentText, commitmentId }) => {
    if (isPreview) {
      console.log('--Preview mode--')
      enqueueMsg(t('commitment_saved_success_message'), 'success', t('success'))
      return
    }
    try {
      const { pageId: commitmentPageId, ...commitment } = await saveCommitmentPromise({
        commitmentId,
        commitmentText,
        pageId: currentPage.id,
        skillBiteId
      })

      const cached = getPageCache(commitmentPageId, skillBiteLanguage)
      const updatedCurrentPage = {
        ...cached,
        blocks: cached.blocks.map(block => {
          if (block.id !== commitmentId) return block
          return {
            ...block,
            commitment
          }
        })
      }

      setPageCache(commitmentPageId, skillBiteLanguage, updatedCurrentPage)

      enqueueMsg(t('commitment_saved_success_message'), 'success', t('success'))
    } catch (err) {
      console.log('Error saving commitment', err)
      enqueueMsg(t('commitment_saved_error_message'), 'error', t('error'))
      throw err
    }
  }, [saveCommitmentPromise, skillBiteId, enqueueMsg, isPreview, currentPage.id, skillBiteLanguage, t])

  const handleSaveCommitmentCheck = useCallback(async ({ commitmentCheckId, commitmentAchieved, commitmentReview }) => {
    if (isPreview) {
      enqueueMsg(t('commitment_check_saved_success_message'), 'success', t('success'))
      return
    }
    try {
      const { pageId: commitmentPageId, ...commitmentCheck } = await saveCommitmentCheckPromise({
        commitmentCheckId,
        commitmentAchieved,
        commitmentReview,
        skillBiteId,
        pageId: currentPage.id
      })

      const cached = getPageCache(commitmentPageId, skillBiteLanguage)
      const updatedCurrentPage = {
        ...cached,
        blocks: cached.blocks.map(block => {
          if (block.id !== commitmentCheckId) return block
          return {
            ...block,
            commitmentCheck
          }
        })
      }

      setPageCache(commitmentPageId, skillBiteLanguage, updatedCurrentPage)
      enqueueMsg(t('commitment_check_saved_success_message'), 'success', t('success'))
    } catch (err) {
      console.log('Error saving commitment-check', err)
      enqueueMsg(t('commitment_check_saved_error_message'), 'error', t('error'))
      throw err
    }
  }, [saveCommitmentCheckPromise, skillBiteId, enqueueMsg, currentPage.id, isPreview, skillBiteLanguage, t])

  const handleGoToDetailPage = () => {
    setErrorModalConfig({
      open: false
    })
    navigate(generatePath(routes['skill-bite'].path, { skillBiteId }))
  }

  const isLoading = getSkillBite.isWorking || getSkillBiteSession.isWorking || !session
  const isLoadingPage = isLoading || getPage.isWorking

  const pageTitle = `MindTools | ${skillBite ? skillBite.name : 'Skill Bites'}`

  const handleSetDefaultLanguage = (language) => {
    getSkillBiteSession.trigger({ skillBiteId, sessionId, from, isPreview, language })
    getSkillBite.trigger({ skillBiteId, from, isPreview, language })
    getPage.trigger({ skillBiteId, sessionId, pageId: currentPage.id, language })
  }

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
      </Helmet>
      <SkillBiteContainer>
        <SkillBiteNavigation isLoading={isLoading}>
          <SkillBiteBreadcrumbs isLoading={isLoading} />
          <Box display='flex' flexDirection='row' marginY={4} justifyContent='space-between' data-test='skill-bite-name-container'>
            <Box display='flex' flexDirection='row' gap='10px' alignItems='center'>
              {isLoading ? <Skeleton width='24px' /> : <SkillBitesIcon />}
              {isLoading ? <Skeleton width='120px' /> : skillBite?.name}
            </Box>
            <Box display='flex' justifyContent='flex-end'>
              <SkillBiteTranslateButton
                loading={getPage.isWorking || getSkillBiteSession.isWorking || getSkillBite.isWorking}
                onClick={() => setTranslateDialogOpen(true)}
              />
            </Box>
          </Box>

          <SkillBiteViewer
            isLoading={isLoadingPage}
            handleSaveCommitment={handleSaveCommitment}
            handleSaveCommitmentCheck={handleSaveCommitmentCheck}
            handleSaveQuiz={handleSaveQuiz}
          />

        </SkillBiteNavigation>
      </SkillBiteContainer>

      <SkillBiteLoadErrorModal
        open={errorModalConfig.open}
        onClose={() => setErrorModalConfig({ open: false })}
        onGoToDetailPage={handleGoToDetailPage}
        title={errorModalConfig.title}
        description={errorModalConfig.description}
      />

      <SkillBiteTranslateDialog
        open={translateDialogOpen}
        onClose={() => setTranslateDialogOpen(false)}
        onTranslate={handleSetDefaultLanguage}
      />
    </>
  )
}

const Page = () => {
  return (
    <ToolkitPrivateRoute
      element={<SkillBiteSessionPage />}
      allowedUserGroups={/clientManager|admin|consumer|enterpriseReader|marketing|ew|editor|cx/}
    />
  )
}

export {
  Page as Component
}
